<template>
    <div class="table-container form-card p-0">
        <div v-if="!isNew" class="filter-container">
            <!-- наименование заголовком над карточкой не выводится — только полем формы (п.4.2) -->
            <div></div>
            <div class="filter-actions">
                <b-button variant="light" @click="back">
                    {{ isEditing ? label('Отменить', 'Болдырмау') : label('Назад', 'Артқа') }}
                </b-button>
                <template v-if="isView && loadedCard">
                    <b-button v-if="isActive && canEdit" variant="primary" @click="startEdit">
                        {{ label('Редактировать', 'Өңдеу') }}
                    </b-button>
                    <!-- удаление — из просмотра, в любом статусе карточки (RQ.eAk.ASSETS.13.01.01, 13.01.05) -->
                    <b-button v-if="canDelete" variant="danger" :title="label('Удалить', 'Жою')" @click="confirmDelete">
                        <i class="icon icon-trash"></i>
                    </b-button>
                </template>
                <b-button v-if="isEditing && loadedCard && canEdit" variant="primary" @click="update">{{ label('Сохранить', 'Сақтау') }}</b-button>
            </div>
        </div>
        <template v-else>
            <div class="asset-card-toolbar">
                <div class="filter-container">
                    <div class="filter-block">
                    </div>
                    <div class="filter-actions">
                        <b-button variant="light" @click="cancel">{{ label('Отмена', 'Бас тарту') }}</b-button>
                        <b-button v-if="canEdit" variant="primary" @click="save">{{ label('Сохранить', 'Сақтау') }}</b-button>
                    </div>
                </div>
            </div>
        </template>
        <div class="asset-form-body">
            <b-row class="no-gutters sticky-header">
                <b-col cols="12" md="6">
                    <b-form-group class="no-line" :label="label('Наименование / назначение *', 'Атауы / мақсаты *')">
                        <div class="d-flex align-items-center">
                            <b-form-input :disabled="isView" :class="{ 'error-field': invalid.name }" v-model="form.name" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                            <span v-if="isNew || loadedCard" class="status-pill ml-2" :class="isActive ? 'status-pill--active' : 'status-pill--archive'">{{ isActive ? label('Активный', 'Белсенді') : label('Архивный', 'Мұрағаттық') }}</span>
                        </div>
                    </b-form-group>

                    <b-form-group class="no-line" :label="label('Инвентарный номер *', 'Түгендеу нөмірі *')">
                        <b-form-input :disabled="isView" :class="{ 'error-field': invalid.invNumber }" v-model="form.invNumber" maxlength="50" :placeholder="ph('Введите', 'Енгізіңіз')" />
                    </b-form-group>

                    <!-- RQ.eAk.ASSETS.03.02.09–10: только чтение, значения проставляет система -->
                    <template v-if="!isNew">
                        <b-form-group class="no-line" :label="label('Источник данных', 'Деректер көзі')">
                            <b-form-input disabled :value="sourceTypeText" />
                        </b-form-group>
                        <b-form-group class="no-line" :label="label('№ отчёта инвентаризации', 'Түгендеу есебінің №')">
                            <b-form-input disabled :value="sourceReportNumber" />
                        </b-form-group>
                    </template>
                </b-col>

                <b-col cols="12" md="6">
                    <b-form-group class="no-line" :label="label('Вид актива *', 'Актив түрі *')">
                        <multiselect :disabled="!isNew" :class="{ 'error-field': invalid.type }"
                            v-model="selectedType"
                            :options="assetTypeList"
                            label="name"
                            track-by="code"
                            :searchable="true"
                            :allow-empty="false"
                            :show-labels="false"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                        </multiselect>
                    </b-form-group>

                    <b-form-group class="no-line" :label="label('Подвид актива *', 'Актив кіші түрі *')">
                        <multiselect :disabled="!isNew" :class="{ 'error-field': invalid.subtype }"
                            v-model="form.subtype"
                            :options="subtypeList"
                            label="name"
                            track-by="code"
                            :show-labels="false"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                        </multiselect>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row class="no-gutters section-header-row">
                <b-col cols="12" md="6">
                    <div class="section-header">{{ label('Идентификация объекта', 'Объектіні сәйкестендіру') }}</div>
                </b-col>
                <b-col cols="12" md="6">
                    <div class="section-header">{{ label('Местонахождение', 'Орналасқан жері') }}</div>
                </b-col>
            </b-row>

            <b-row class="no-gutters section-header-row">
                <!--  Левая сторона  -->
                <b-col cols="12" md="6">
                    <!--  Идентификация  -->
                    <b-form-group :label="label('Тип балансодержателя *', 'Баланс ұстаушының түрі *')">
                        <div class="owner-type-block">
                            <b-form-radio :disabled="!isNew" v-model="form.balanceholderType"
                                          name="balanceholder-type"
                                          value="gu"
                                          @change="onBalanceholderTypeChange">ГУ
                            </b-form-radio>
                            <b-form-radio :disabled="!isNew" v-model="form.balanceholderType"
                                          name="balanceholder-type"
                                          value="kgkp"
                                          @change="onBalanceholderTypeChange">КГКП
                            </b-form-radio>
                        </div>
                    </b-form-group>
                    <b-form-group :label="label('АБП *', 'ББӘ *')">
                        <multiselect :disabled="!isNew" :class="{ 'error-field': invalid.abp }"
                            v-model="form.abp"
                            :options="abpList"
                            label="text"
                            track-by="code"
                            :show-labels="false"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                            @input="onAbpOrTypeChange"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                        </multiselect>
                    </b-form-group>
                    <b-form-group :label="label('Балансодержатель *', 'Баланс ұстаушы *')">
                        <multiselect
                            :class="{ 'error-field': invalid.balanceholder }"
                            v-model="form.balanceholder"
                            :options="balanceholderOptions"
                            label="text"
                            track-by="code"
                            :show-labels="false"
                            :disabled="!isNew || !form.abp"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                        </multiselect>
                    </b-form-group>
                    <b-form-group :label="label('Код права на имущество', 'Мүлікке құқық коды')">
                        <multiselect :disabled="isView"
                            v-model="form.ownershipType"
                            :options="ownershipTypeList"
                            label="name"
                            track-by="code"
                            :show-labels="false"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                        </multiselect>
                    </b-form-group>
                    <b-form-group :label="label('Дата принятия на баланс *', 'Балансқа қабылдау күні *')">
                        <date-picker :disabled="isView"
                            :class="{ 'error-field': invalid.balanceDate }"
                            :placeholder="ph('дд.мм.гггг', 'кк.аа.жжжж')"
                            v-model="form.balanceDate"
                            format="DD.MM.YYYY"
                        />
                    </b-form-group>
                    <!--  Стоимостные показатели  -->
                    <div class="section-header">{{ label('Стоимостные показатели', 'Құн көрсеткіштері') }}
                        <span class="section-header-unit">теңге</span>
                    </div>
                    <b-form-group :label="label('Первоначальная стоимость', 'Бастапқы құны')">
                        <b-form-input :disabled="isView" v-model.number="form.initialCost" type="number" :placeholder="ph('Введите', 'Енгізіңіз')" />
                    </b-form-group>
                    <b-form-group :label="label('Накопленная амортизация', 'Жинақталған тозу')">
                        <b-form-input :disabled="isView" v-model.number="form.depreciation" type="number" :placeholder="ph('Введите', 'Енгізіңіз')" />
                    </b-form-group>
                    <b-form-group :label="label('Убыток от обесценения', 'Құнсыздануынан болған залал')">
                        <b-form-input :disabled="isView" v-model.number="form.impairmentLoss" type="number" :placeholder="ph('Введите', 'Енгізіңіз')" />
                    </b-form-group>
                    <b-form-group :label="label('Балансовая стоимость', 'Баланстық құны')">
                        <b-form-input :value="bookValue" disabled />
                    </b-form-group>
                    <b-form-group :label="label('Сумма переоценки (+/-)', 'Қайта бағалау сомасы (+/-)')">
                        <b-form-input :disabled="!isNew" v-model.number="form.revaluation" type="number" :placeholder="ph('Введите', 'Енгізіңіз')" />
                    </b-form-group>
                    <b-form-group :label="label('Балансовая стоимость после переоценки', 'Қайта бағалаудан кейінгі баланстық құны')">
                        <b-form-input :value="bookValueAfterReval" disabled />
                    </b-form-group>
                    <b-form-group :label="label('Износ', 'Тозу')">
                        <b-form-input :disabled="isView" v-model.number="form.wearAmount" type="number" :placeholder="ph('Введите', 'Енгізіңіз')" />
                    </b-form-group>
                </b-col>
                <!--  Правая сторона  -->
                <b-col cols="12" md="6">
                    <!--  Местонахождение  -->
                    <b-form-group :label="label('Регион', 'Өңір')">
                        <multiselect :disabled="isView"
                            v-model="form.region"
                            :options="regionList"
                            :custom-label="regionLabel"
                            track-by="code"
                            :show-labels="false"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                        </multiselect>
                    </b-form-group>
                    <b-form-group :label="label('Адрес', 'Мекенжай')">
                        <b-form-input :disabled="isView" v-model="form.address" :placeholder="ph('Введите', 'Енгізіңіз')" />
                    </b-form-group>
                    <!--  Основание поступления  -->
                    <div class="section-header">{{ label('Основание поступления', 'Түсу негізі') }}</div>
                    <b-form-group
                        :label="label('Основание поступления в госсобственность', 'Мемлекеттік меншікке түсу негізі')">
                        <multiselect :disabled="isView"
                            v-model="form.acquisitionBasis"
                            :options="acquisitionBasisList"
                            label="name"
                            track-by="code"
                            :show-labels="false"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                        </multiselect>
                    </b-form-group>
                    <b-form-group
                        :label="label('Примечание поступления в госсобственность', 'Мемлекеттік меншікке түсу негізіне ескертпе')">
                        <b-form-textarea :disabled="isView" v-model="form.acquisitionNote" :placeholder="ph('Введите', 'Енгізіңіз')" rows="3"/>
                    </b-form-group>
                    <b-form-group :label="label('Основание поступления на баланс', 'Балансқа түсу негізі')">
                        <multiselect :disabled="isView"
                            v-model="form.balanceBasis"
                            :options="basisList"
                            label="name"
                            track-by="code"
                            :show-labels="false"
                            :placeholder="ph('Выбрать', 'Таңдаңыз')"
                        >
                            <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                            <span slot="noOptions">{{ label('Список пуст.', 'Тізім бос.') }}</span>
                        </multiselect>
                    </b-form-group>
                    <b-form-group :label="label('Примечание', 'Ескертпе')">
                        <b-form-textarea :disabled="isView" v-model="form.note" :placeholder="ph('Введите', 'Енгізіңіз')" rows="3"/>
                    </b-form-group>
                    <!--  Блок reg_doc «Регистрационные документы»   -->
                    <template v-if="hasRegDoc">
                        <div class="section-header">{{ label('Регистрационные документы', 'Тіркеу құжаттары') }}</div>
                        <b-form-group :label="label('Тип рег. документа', 'Тіркеу құжатының түрі')">
                            <multiselect :disabled="isView"
                                v-model="form.regDocTypeId"
                                :options="regDocTypeList"
                                label="name"
                                track-by="code"
                                :show-labels="false"
                                :placeholder="ph('Выбрать', 'Таңдаңыз')"
                            >
                                <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                            </multiselect>
                        </b-form-group>
                        <b-form-group :label="label('Номер рег. документа', 'Тіркеу құжатының нөмірі')">
                            <b-form-input :disabled="isView" v-model="form.regDocNumber" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Дата выдачи рег. документа', 'Тіркеу құжатының берілген күні')">
                            <date-picker :disabled="isView"
                                :placeholder="ph('дд.мм.гггг', 'кк.аа.жжжж')"
                                v-model="form.regDocDate"
                                format="DD.MM.YYYY"
                            />
                        </b-form-group>
                        <b-form-group :label="label('Рег. документ выдан', 'Тіркеу құжатын берген орган')">
                            <b-form-input :disabled="isView" v-model="form.regDocIssuer" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Кадастровый номер', 'Кадастрлық нөмір')">
                            <b-form-input :disabled="isView" v-model="form.cadastralNumber" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Сведения о техпаспорте', 'Техникалық паспорт туралы мәлімет')">
                            <multiselect :disabled="isView"
                                v-model="form.techpassportId"
                                :options="techpassportList"
                                label="name"
                                track-by="code"
                                :show-labels="false"
                                :placeholder="ph('Выбрать', 'Таңдаңыз')"
                            >
                                <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                            </multiselect>
                        </b-form-group>
                    </template>
                    <!--  Блок building «Характеристики здания / сооружения»  -->
                    <template v-if="hasBuilding">
                        <div class="section-header">
                            {{ label('Характеристики здания / сооружения', 'Ғимарат / имараттың сипаттамасы') }}
                        </div>
                        <b-form-group :label="label('Номер корпуса, строения', 'Корпус, ғимарат нөмірі')">
                            <b-form-input :disabled="isView" v-model="form.buildingNumber" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Объём здания (куб.м)', 'Ғимарат көлемі (текше м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingVolume" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Число этажей', 'Қабаттар саны')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingFloors" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Число квартир (шт.)', 'Пәтерлер саны (дана)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingFlats" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Число комнат, помещений (шт.)', 'Бөлмелер саны (дана)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingRooms" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Общая площадь (кв.м)', 'Жалпы алаңы (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaTotal" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Полезная площадь (кв.м)', 'Пайдалы алаңы (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaUseful" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Вспомогательная площадь (кв.м)', 'Қосалқы алаң (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaAuxiliary" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Площадь застройки (кв.м)', 'Құрылыс салу алаңы (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaFootprint" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Площадь балконов и террас (кв.м)', 'Балкон және терраса алаңы (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaBalcony" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Площадь нежилых помещений (кв.м)', 'Тұрғын емес үй-жайлар алаңы (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaNonResidential" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Площадь, занимаемая организацией (кв.м)', 'Ұйым алып тұрған алаң (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaOccupied" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Площадь, сдаваемая в аренду (кв.м)', 'Жалға берілетін алаң (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaRented" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group
                            :label="label('Свободная площадь для сдачи в аренду (кв.м)', 'Жалға беруге бос алаң (шаршы м)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingAreaRentAvailable" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Материал постройки', 'Құрылыс материалы')">
                            <multiselect :disabled="isView"
                                v-model="form.buildingMaterialId"
                                :options="buildingMaterialList"
                                label="name"
                                track-by="code"
                                :show-labels="false"
                                :placeholder="ph('Выбрать', 'Таңдаңыз')"
                            >
                                <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                            </multiselect>
                        </b-form-group>
                        <b-form-group :label="label('Год постройки', 'Салынған жылы')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingYear" type="number" :placeholder="ph('гггг', 'жжжж')"/>
                        </b-form-group>
                        <b-form-group :label="label('Тип сооружения', 'Имарат түрі')">
                            <multiselect :disabled="isView"
                                v-model="form.structureTypeId"
                                :options="structureTypeList"
                                label="name"
                                track-by="code"
                                :show-labels="false"
                                :placeholder="ph('Выбрать', 'Таңдаңыз')"
                            >
                                <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                            </multiselect>
                        </b-form-group>
                        <b-form-group :label="label('Протяжённость (км)', 'Ұзындығы (км)')">
                            <b-form-input :disabled="isView" v-model.number="form.buildingLengthKm" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                    </template>
                    <!--  Блок vehicle «Характеристики транспортного средства»   -->
                    <template v-if="hasVehicle">
                        <div class="section-header">
                            {{ label('Характеристики транспортного средства', 'Көлік құралының сипаттамасы') }}
                        </div>
                        <b-form-group :label="label('Марка', 'Маркасы')">
                            <b-form-input :disabled="isView" v-model="form.vehicleBrand" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Государственный номер', 'Мемлекеттік нөмірі')">
                            <b-form-input :disabled="isView" v-model="form.vehicleStateNumber" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Номер двигателя', 'Қозғалтқыш нөмірі')">
                            <b-form-input :disabled="isView" v-model="form.vehicleEngineNumber" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Номер кузова', 'Кузов нөмірі')">
                            <b-form-input :disabled="isView" v-model="form.vehicleBodyNumber" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Цвет', 'Түсі')">
                            <b-form-input :disabled="isView" v-model="form.vehicleColor" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Объём двигателя (куб.см)', 'Қозғалтқыш көлемі (текше см)')">
                            <b-form-input :disabled="isView" v-model.number="form.vehicleEngineVolume" type="number"
                                          :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Пробег (км)', 'Жүрісі (км)')">
                            <b-form-input :disabled="isView" v-model.number="form.vehicleMileage" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Год выпуска', 'Шығарылған жылы')">
                            <b-form-input :disabled="isView" v-model.number="form.vehicleProductionYear" type="number" :placeholder="ph('гггг', 'жжжж')"/>
                        </b-form-group>
                    </template>
                    <!--  Блок equipment «Характеристики оборудования / инвентаря»  -->
                    <template v-if="hasEquipment">
                        <div class="section-header">{{label('Характеристики оборудования', 'Жабдықтың сипаттамасы')}}</div>
                        <b-form-group :label="label('Марка / тип', 'Маркасы / түрі')">
                            <b-form-input :disabled="isView" v-model="form.equipmentBrandType" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Год приобретения', 'Сатып алынған жылы')">
                            <b-form-input :disabled="isView" v-model.number="form.equipmentAcquisitionYear" type="number"
                                          :placeholder="ph('гггг', 'жжжж')"/>
                        </b-form-group>
                        <b-form-group :label="label('Год ввода в эксплуатацию', 'Пайдалануға берілген жылы')">
                            <b-form-input :disabled="isView" v-model.number="form.equipmentCommissioningYear" type="number"
                                          :placeholder="ph('гггг', 'жжжж')"/>
                        </b-form-group>
                    </template>
                    <!--  Блок land «Характеристики земельного участка»   -->
                    <template v-if="hasLand">
                        <div class="section-header">
                            {{ label('Характеристики земельного участка', 'Жер учаскесінің сипаттамасы') }}
                        </div>
                        <b-form-group :label="label('Площадь (га)', 'Ауданы (га)')">
                            <b-form-input :disabled="isView" v-model.number="form.landAreaHa" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Целевое назначение', 'Мақсатты арналымы')">
                            <multiselect :disabled="isView"
                                v-model="form.landPurposeId"
                                :options="landPurposeList"
                                label="name"
                                track-by="code"
                                :show-labels="false"
                                :placeholder="ph('Выбрать', 'Таңдаңыз')"
                            >
                                <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                            </multiselect>
                        </b-form-group>
                        <b-form-group :label="label('Делимость', 'Бөлінгіштігі')">
                            <b-form-checkbox :disabled="isView" v-model="form.landDivisibility">{{
                                    label('Делимый', 'Бөлінеді')
                                }}
                            </b-form-checkbox>
                        </b-form-group>
                        <b-form-group
                            :label="label('Ограничение в использовании и обременения', 'Пайдалануға шектеу және ауыртпалықтар')">
                            <b-form-textarea :disabled="isView" v-model="form.landRestrictions" :placeholder="ph('Введите', 'Енгізіңіз')" rows="2"/>
                        </b-form-group>
                        <b-form-group :label="label('Срок землепользования, с', 'Жер пайдалану мерзімі — бастап')">
                            <date-picker :disabled="isView"
                                :placeholder="ph('дд.мм.гггг', 'кк.аа.жжжж')"
                                v-model="form.landUseDateFrom"
                                format="DD.MM.YYYY"
                            />
                        </b-form-group>
                        <b-form-group :label="label('Срок землепользования, по', 'Жер пайдалану мерзімі — дейін')">
                            <date-picker :disabled="isView"
                                :placeholder="ph('дд.мм.гггг', 'кк.аа.жжжж')"
                                v-model="form.landUseDateTo"
                                format="DD.MM.YYYY"
                            />
                        </b-form-group>
                        <b-form-group
                            :label="label('Размер арендной платы (тг./год)', 'Жалдау ақысының мөлшері (тг./жыл)')">
                            <b-form-input :disabled="isView" v-model.number="form.landRentAmount" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                    </template>
                    <!--  Блок network «Характеристики передаточных устройств (сетей)»  -->
                    <template v-if="hasNetwork">
                        <div class="section-header">{{
                                label('Характеристики передаточных устройств (сетей)', 'Беру құрылғыларының (желілердің) сипаттамасы')
                            }}
                        </div>
                        <b-form-group :label="label('Общая протяжённость провода (км)', 'Сымның жалпы ұзындығы (км)')">
                            <b-form-input :disabled="isView" v-model.number="form.networkWireLengthKm" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Общая протяжённость кабеля (км)', 'Кабельдің жалпы ұзындығы (км)')">
                            <b-form-input :disabled="isView" v-model.number="form.networkCableLengthKm" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group
                            :label="label('Распределительные пункты электролинии (шт.)', 'Электр желісінің тарату пункттері (дана)')">
                            <b-form-input :disabled="isView" v-model.number="form.networkDistributionPoints" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Количество простых ТП (шт.)', 'Қарапайым ТҚ саны (дана)')">
                            <b-form-input :disabled="isView" v-model.number="form.networkTpSimple" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Количество комплектных ТП (шт.)', 'Кешенді ТҚ саны (дана)')">
                            <b-form-input :disabled="isView" v-model.number="form.networkTpComplete" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Количество комплектных наружных ТП (шт.)', 'Кешенді сыртқы ТҚ саны (дана)')">
                            <b-form-input :disabled="isView" v-model.number="form.networkTpOutdoor" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                        <b-form-group :label="label('Количество абонентов', 'Абоненттер саны')">
                            <b-form-input :disabled="isView" v-model.number="form.networkSubscribers" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                    </template>
                    <!--  Блок condition «Состояние и эксплуатация»  -->
                    <template v-if="hasCondition">
                        <div class="section-header">
                            {{ label('Состояние и эксплуатация', 'Жай-күйі және пайдаланылуы') }}
                        </div>
                        <b-form-group :label="label('Сведения о годности к эксплуатации', 'Пайдалануға жарамдылығы туралы мәлімет')">
                            <multiselect :disabled="isView"
                                v-model="form.usability"
                                :options="usabilityList"
                                label="name"
                                track-by="code"
                                :show-labels="false"
                                :placeholder="ph('Выбрать', 'Таңдаңыз')"
                            >
                                <span slot="noResult">{{ label('Ничего не найдено. Измените условия поиска.', 'Ештеңе табылмады. Іздеу шарттарын өзгертіңіз.') }}</span>
                            </multiselect>
                        </b-form-group>
                        <b-form-group :label="label('Дата ввода в эксплуатацию', 'Пайдалануға берілген күні')">
                            <date-picker :disabled="isView"
                                :placeholder="ph('дд.мм.гггг', 'кк.аа.жжжж')"
                                v-model="form.commissioningDate"
                                format="DD.MM.YYYY"
                            />
                        </b-form-group>
                        <b-form-group :label="label('Краткое описание технического состояния', 'Техникалық жай-күйінің қысқаша сипаттамасы')">
                            <b-form-textarea :disabled="isView" v-model="form.techStateDesc" maxlength="1000" :placeholder="ph('Введите', 'Енгізіңіз')" rows="2"/>
                        </b-form-group>
                        <b-form-group :label="label('Общая характеристика (описание) имущества', 'Мүліктің жалпы сипаттамасы')">
                            <b-form-textarea :disabled="isView" v-model="form.generalDesc" maxlength="1000" :placeholder="ph('Введите', 'Енгізіңіз')" rows="2"/>
                        </b-form-group>
                    </template>
                    <!--  Блок repair «Ремонт и капитальные вложения»   -->
                    <template v-if="hasRepair">
                        <div class="section-header">
                            {{ label('Ремонт и капитальные вложения', 'Жөндеу және күрделі салымдар') }}
                        </div>
                        <b-form-group
                            :label="label('Затраты на кап. ремонт — дата проведения', 'Күрделі жөндеу шығыны — жүргізілген күні')">
                            <date-picker :disabled="isView"
                                :placeholder="ph('дд.мм.гггг', 'кк.аа.жжжж')"
                                v-model="form.repairDate"
                                format="DD.MM.YYYY"
                            />
                        </b-form-group>
                        <b-form-group
                            :label="label('Затраты на кап. ремонт — общая стоимость (тг.)', 'Күрделі жөндеу шығыны — жалпы құны (тг.)')">
                            <b-form-input :disabled="isView" v-model.number="form.repairCost" type="number" :placeholder="ph('Введите', 'Енгізіңіз')"/>
                        </b-form-group>
                    </template>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import moment from "moment/moment";
export default {
    name: 'AddAssetCard',
    components: {DatePicker},
    async created() {
        await this.getAccessLevel();
        this.blockLoads = Promise.resolve();
        if (this.isNew) {
            // область инстанса нужна списку АБП (RQ.eAk.ASSETS.01.04.07) — getObl первым
            await this.getObl();
            await this.loadAbpList();
            await this.loadAssetTypeList();
            return;
        }
        await this.loadCard();
    },
    data() {
        return {
            // уровень доступа к модулю «Реестр госактивов» (003.010): 1 — просмотр, 2/3 — создание и правка, 4 — ещё и удаление
            access_level: 1,
            abpList: [],
            guGkkpList: [],
            ownershipTypeList: [],
            regionList: [],
            acquisitionBasisList: [],
            basisList: [],
            usabilityList: [],
            regDocTypeList: [],
            techpassportList: [],
            buildingMaterialList: [],
            structureTypeList: [],
            landPurposeList: [],
            assetTypeList: [],
            allAssetSubtypes: [],
            selectedType: null,
            // карточка из get_asset_card — для отката правок при выходе без сохранения
            loadedCard: null,
            statusId: 1,
            // снимок формы после загрузки — по нему определяются несохранённые изменения
            snapshot: null,
            invalid: { name: false, invNumber: false, type: false, subtype: false, abp: false, balanceholder: false, balanceDate: false },
            assetCard: [],
            regDocDictsLoaded: false,
            buildingDictsLoaded: false,
            landDictsLoaded: false,

            form: {
                invNumber: '',
                name: '',
                ownershipType: null,
                subtype: null,
                abp: null,
                balanceholderType: 'gu',
                balanceholder: null,
                balanceDate: null,
                region: null,
                address: '',
                initialCost: null,
                depreciation: null,
                impairmentLoss: null,
                revaluation: null,
                wearAmount: null,
                acquisitionBasis: null,
                acquisitionNote: '',
                balanceBasis: null,
                note: '',
                // блок reg_doc
                regDocTypeId: null,
                regDocNumber: '',
                regDocDate: null,
                regDocIssuer: '',
                cadastralNumber: '',
                techpassportId: null,
                // блок building
                buildingNumber: '',
                buildingVolume: null,
                buildingFloors: null,
                buildingFlats: null,
                buildingRooms: null,
                buildingAreaTotal: null,
                buildingAreaUseful: null,
                buildingAreaAuxiliary: null,
                buildingAreaFootprint: null,
                buildingAreaBalcony: null,
                buildingAreaNonResidential: null,
                buildingAreaOccupied: null,
                buildingAreaRented: null,
                buildingAreaRentAvailable: null,
                buildingMaterialId: null,
                buildingYear: null,
                structureTypeId: null,
                buildingLengthKm: null,
                // блок vehicle
                vehicleBrand: '',
                vehicleStateNumber: '',
                vehicleEngineNumber: '',
                vehicleBodyNumber: '',
                vehicleColor: '',
                vehicleEngineVolume: null,
                vehicleMileage: null,
                vehicleProductionYear: null,
                // блок equipment
                equipmentBrandType: '',
                equipmentAcquisitionYear: null,
                equipmentCommissioningYear: null,
                // блок land
                landAreaHa: null,
                landPurposeId: null,
                landDivisibility: false,
                landRestrictions: '',
                landUseDateFrom: null,
                landUseDateTo: null,
                landRentAmount: null,
                // блок network
                networkWireLengthKm: null,
                networkCableLengthKm: null,
                networkDistributionPoints: null,
                networkTpSimple: null,
                networkTpComplete: null,
                networkTpOutdoor: null,
                networkSubscribers: null,
                // блок condition
                usability: null,
                commissioningDate: null,
                techStateDesc: '',
                generalDesc: '',
                // блок repair
                repairDate: null,
                repairCost: null,
            }
        };
    },
    computed: {
        canEdit() {
            return this.access_level >= 2;
        },
        canDelete() {
            return this.access_level === 3;
        },
        isNew() {
            return !this.$route.params.id;
        },
        // просмотр карточки (UC.eAk.ASSETS.03): все поля disabled; снимется режимом редактирования (UC.eAk.ASSETS.12)
        isView() {
            return !this.isNew && !this.isEditing;
        },
        isActive() {
            return this.statusId === 1;
        },
        // режим редактирования (UC.eAk.ASSETS.12) задаётся query ?mode=edit — так он одинаково открывается
        // из реестра и с карточки и переживает обновление страницы; архивную карточку не редактируем
        isEditing() {
            return !this.isNew && this.$route.query.mode === 'edit' && this.isActive;
        },
        // откуда открыто редактирование — туда и возвращаемся (RQ.eAk.ASSETS.12.05.04–05)
        fromRegistry() {
            return this.$route.query.from === 'registry';
        },
        // источник данных карточки: report — отчёт инвентаризации, manual — ручной ввод (RQ.eAk.ASSETS.03.02.09)
        sourceTypeText() {
            const type = this.loadedCard && this.loadedCard.source_type;
            if (type === 'report') return this.label('Отчёт инвентаризации', 'Түгендеу есебі');
            if (type === 'manual') return this.label('Ручной ввод', 'Қолмен енгізу');
            return '—';
        },
        // номер отчёта-источника; у карточки ручного ввода — прочерк (RQ.eAk.ASSETS.03.02.10)
        sourceReportNumber() {
            const card = this.loadedCard;
            return card && card.source_type === 'report' && card.source_report_id ? String(card.source_report_id) : '—';
        },
        balanceholderOptions() {
            if (!this.form.abp) return [];
            return this.guGkkpList
                .filter(item => item.abp === this.form.abp.code)
                .map(item => ({
                    code: item.code,
                    name: this.$i18n.locale === 'kk' ? (item.name_kk || item.name_ru) : (item.name_ru || item.name_kk),
                    text: `${item.code} - ${this.$i18n.locale === 'kk' ? (item.name_kk || item.name_ru) : (item.name_ru || item.name_kk)}`
                }));
        },
        hasRegDoc() {
            return !!this.form.subtype && this.form.subtype.hasRegDoc;
        },
        hasBuilding() {
            return !!this.form.subtype && this.form.subtype.hasBuilding;
        },
        hasVehicle() {
            return !!this.form.subtype && this.form.subtype.hasVehicle;
        },
        hasEquipment() {
            return !!this.form.subtype && this.form.subtype.hasEquipment;
        },
        hasLand() {
            return !!this.form.subtype && this.form.subtype.hasLand;
        },
        hasNetwork() {
            return !!this.form.subtype && this.form.subtype.hasNetwork;
        },
        hasCondition() {
            return !!this.form.subtype && this.form.subtype.hasCondition;
        },
        hasRepair() {
            return !!this.form.subtype && this.form.subtype.hasRepair;
        },
        subtypeList() {
            if (!this.selectedType) return [];
            return (this.allAssetSubtypes || [])
                .filter((st) => st.type_code === this.selectedType.code)
                .map((st) => ({
                    code: st.code,
                    name: this.$i18n.locale === 'kk'
                        ? (st.name_kk || st.name_ru)
                        : (st.name_ru || st.name_kk),
                    hasRegDoc: st.has_reg_doc,
                    hasBuilding: st.has_building,
                    hasVehicle: st.has_vehicle,
                    hasEquipment: st.has_equipment,
                    hasLand: st.has_land,
                    hasNetwork: st.has_network,
                    hasCondition: st.has_condition,
                    hasRepair: st.has_repair
                }));
        },
        bookValue() {
            // RQ.eAk.ASSETS.01.03.01: Первоначальная стоимость − Накопленная амортизация − Убыток от обесценения
            const initial = this.form.initialCost || 0;
            const depreciation = this.form.depreciation || 0;
            const impairment = this.form.impairmentLoss || 0;
            return Number((initial - depreciation - impairment).toFixed(2));
        },
        bookValueAfterReval() {
            // RQ.eAk.ASSETS.01.03.02: Балансовая стоимость + Сумма переоценки
            return Number((this.bookValue + (this.form.revaluation || 0)).toFixed(2));
        }
    },
    watch: {
        // тот же маршрут для создания и просмотра — компонент не пересоздаётся, поэтому после сохранения
        // (переход card -> card/:id) карточку нужно загрузить явно
        '$route.params.id'(id) {
            if (id) this.loadCard();
        },
        // при смене вида актива сбрасываем ранее выбранный подвид,
        // т.к. он может не принадлежать новому type_code
        selectedType(type) {
            this.form.subtype = null;
            if (type) this.invalid.type = false;
        },
        'form.subtype'(subtype) {
            this.loadBlockDicts(subtype);
            if (subtype) this.invalid.subtype = false;
        },
        // снимаем подсветку обязательного поля, как только оно заполнено
        'form.name'(v) { if (String(v || '').trim()) this.invalid.name = false; },
        'form.invNumber'(v) { if (String(v || '').trim()) this.invalid.invNumber = false; },
        'form.abp'(v) { if (v) this.invalid.abp = false; },
        'form.balanceholder'(v) { if (v) this.invalid.balanceholder = false; },
        'form.balanceDate'(v) { if (v) this.invalid.balanceDate = false; },
        // названия справочников подставляются при загрузке — при смене языка перечитываем их и перепривязываем выбор
        '$i18n.locale'() {
            this.relocalize();
        }
    },
    methods: {
        async getAccessLevel() {
            try {
                const response = await fetch(`/api-py/user-modules/${this.$store.getters.user_uuid}/003.010`);
                const modules_access = await response.json();
                if (modules_access.length > 1) {
                    this.makeToast('danger', 'Ошибка', 'У пользователя несколько записей');
                }
                // при нескольких записях берём максимальный уровень; без записи — просмотр
                this.access_level = modules_access.length
                    ? Math.max(...modules_access.map(ma => ma.access_level))
                    : 1;
            } catch (error) {
                this.makeToast('danger', 'Ошибка запроса load_user_access_level', error.toString());
            }
        },
        label(ru, kk) {
            return this.$i18n.locale === 'kk' ? kk : ru;
        },
        ph(ru, kk) {
            return this.isView ? '—' : this.label(ru, kk);
        },
        onAbpOrTypeChange() {
            // RQ.eAk.ASSETS.01.06.05: при смене АБП или типа балансодержателя — сбросить балансодержателя
            this.form.balanceholder = null;
        },
        async onBalanceholderTypeChange() {
            // RQ.eAk.ASSETS.01.06.05: смена типа балансодержателя сбрасывает ранее выбранного балансодержателя;
            // АБП тоже сбрасываем — список АБП зависит от типа и выбранный может в него не входить
            this.form.abp = null;
            this.onAbpOrTypeChange();
            await this.loadAbpList();
        },
        async loadCard() {
            try {
                // область инстанса из getObl нужна списку регионов (поле «Регион» правится)
                const cardRequest = fetch('/api-py/get_asset_card/' + this.$route.params.id
                    + '?user_id=' + encodeURIComponent(this.$store.getters.user_uuid));
                const coreDicts = Promise.all([this.loadAssetTypeList(), this.getObl()]);
                const response = await cardRequest;
                if (response.status !== 200) {
                    // NTF.eAk.ASSETS.03.01 — модальное окно, сведения карточки не выводим, возвращаем в реестр
                    await this.$bvModal.msgBoxOk(
                        this.label('Карточка объекта недоступна.', 'Объект карточкасы қолжетімсіз.'),
                        { centered: true });
                    this.cancel();
                    return;
                }
                const card = await response.json();
                this.form.balanceholderType = card.balanceholder_kgkp_bin ? 'kgkp' : 'gu';
                // АБП и балансодержатель в просмотре и правке заблокированы — списки АБП и ГУ/КГКП не грузим,
                // нужные значения (с названиями) приходят в самой карточке
                this.buildHolderLists(card);
                // справочники блоков нужны только по флагам подвида, а они приходят в самой карточке
                await this.loadBlockDicts({
                    hasRegDoc: card.has_reg_doc,
                    hasBuilding: card.has_building,
                    hasLand: card.has_land
                });
                await coreDicts;
                this.loadedCard = card;
                this.statusId = card.status_id;
                await this.fillForm(card);
                await this.$nextTick();
                this.snapshot = this.serializeForm();
            } catch (e) {
                this.makeToast('danger', this.label('Предупреждение', 'Ескерту'), e.toString());
            }
        },
        // списки АБП и балансодержателя из одной записи карточки — вместо запроса get_abp_by_user_gu_gkkp;
        // названия берутся на текущем языке, при смене языка список строится заново (relocalize)
        buildHolderLists(card) {
            if (!card) return;
            const pick = (ru, kk) => (this.$i18n.locale === 'kk' ? (kk || ru) : (ru || kk));
            const abpName = pick(card.abp_name_ru, card.abp_name_kk);
            this.abpList = card.abp_code === null || card.abp_code === undefined
                ? []
                : [{ code: card.abp_code, name: abpName, text: `${card.abp_code} - ${abpName}` }];
            const isKgkp = !!card.balanceholder_kgkp_bin;
            const code = isKgkp ? card.balanceholder_kgkp_bin : card.balanceholder_gu_code;
            this.guGkkpList = code
                ? [{
                    code,
                    abp: card.abp_code,
                    name_ru: isKgkp ? card.balanceholder_kgkp_name_ru : card.balanceholder_gu_name_ru,
                    name_kk: isKgkp ? card.balanceholder_kgkp_name_kk : card.balanceholder_gu_name_kk
                }]
                : [];
        },
        // подгружаем справочники нужных вкладок только когда подвид их требует,
        // и только один раз за время жизни компонента; промис — чтобы fillForm мог дождаться загрузки
        loadBlockDicts(subtype) {
            const loads = [];
            if (!subtype) return Promise.resolve();
            if (subtype.hasRegDoc && !this.regDocDictsLoaded) {
                this.regDocDictsLoaded = true;
                loads.push(this.loadRegDocTypeList(), this.loadTechpassportList());
            }
            if (subtype.hasBuilding && !this.buildingDictsLoaded) {
                this.buildingDictsLoaded = true;
                loads.push(this.loadBuildingMaterialList(), this.loadStructureTypeList());
            }
            if (subtype.hasLand && !this.landDictsLoaded) {
                this.landDictsLoaded = true;
                loads.push(this.loadLandList());
            }
            // загрузки, запущенные раньше (loadCard стартует их по флагам карточки), тоже ждём —
            // иначе повторный вызов увидит флаги *DictsLoaded и вернётся, не дождавшись данных
            this.blockLoads = Promise.all([this.blockLoads, ...loads]);
            return this.blockLoads;
        },
        regionLabel(option) {
            return this.$i18n.locale === 'kk' ? (option.name_kk || option.name_ru) : (option.name_ru || option.name_kk);
        },
        // смена языка: справочники строятся с названиями текущего языка, выбранные в форме опции — старые объекты;
        // перечитываем списки и ставим выбор заново по кодам, остальные значения формы не трогаем
        async relocalize() {
            const f = this.form;
            const codeOf = (opt) => (opt ? opt.code : null);
            const keep = {
                type: codeOf(this.selectedType),
                subtype: codeOf(f.subtype),
                ownershipType: codeOf(f.ownershipType),
                abp: codeOf(f.abp),
                balanceholder: codeOf(f.balanceholder),
                region: codeOf(f.region),
                acquisitionBasis: codeOf(f.acquisitionBasis),
                balanceBasis: codeOf(f.balanceBasis),
                regDocTypeId: codeOf(f.regDocTypeId),
                techpassportId: codeOf(f.techpassportId),
                buildingMaterialId: codeOf(f.buildingMaterialId),
                structureTypeId: codeOf(f.structureTypeId),
                landPurposeId: codeOf(f.landPurposeId),
                usability: codeOf(f.usability)
            };
            const wasDirty = this.isDirty();
            const loads = [this.loadAssetTypeList()];
            // создание — список АБП с сервера; просмотр и правка — значения из карточки на новом языке
            if (this.isNew) loads.push(this.loadAbpList());
            else this.buildHolderLists(this.loadedCard);
            if (this.regDocDictsLoaded) loads.push(this.loadRegDocTypeList(), this.loadTechpassportList());
            if (this.buildingDictsLoaded) loads.push(this.loadBuildingMaterialList(), this.loadStructureTypeList());
            if (this.landDictsLoaded) loads.push(this.loadLandList());
            await Promise.all(loads);

            // смена вида актива сбрасывает подвид (watcher selectedType) — восстанавливаем после него
            this.selectedType = this.byCode(this.assetTypeList, keep.type);
            await this.$nextTick();
            f.subtype = this.byCode(this.subtypeList, keep.subtype);
            f.ownershipType = this.byCode(this.ownershipTypeList, keep.ownershipType);
            f.abp = this.byCode(this.abpList, keep.abp);
            f.region = this.byCode(this.regionList, keep.region);
            f.acquisitionBasis = this.byCode(this.acquisitionBasisList, keep.acquisitionBasis);
            f.balanceBasis = this.byCode(this.basisList, keep.balanceBasis);
            f.balanceholder = this.byCode(this.balanceholderOptions, keep.balanceholder);
            f.regDocTypeId = this.byCode(this.regDocTypeList, keep.regDocTypeId);
            f.techpassportId = this.byCode(this.techpassportList, keep.techpassportId);
            f.buildingMaterialId = this.byCode(this.buildingMaterialList, keep.buildingMaterialId);
            f.structureTypeId = this.byCode(this.structureTypeList, keep.structureTypeId);
            f.landPurposeId = this.byCode(this.landPurposeList, keep.landPurposeId);
            f.usability = this.byCode(this.usabilityList, keep.usability);
            // названия в снимке изменились, а правок пользователя нет — не считать смену языка изменением
            await this.$nextTick();
            if (!wasDirty) this.snapshot = this.serializeForm();
        },
        // multiselect ждёт объект из списка опций, а из БД приходит код
        byCode(list, code) {
            if (code === null || code === undefined) return null;
            return list.find(item => String(item.code) === String(code)) || null;
        },
        toDate(value) {
            return value ? moment(value).toDate() : null;
        },
        // раскладывает карточку из get_asset_card по полям формы; справочники ядра уже загружены
        async fillForm(card) {
            this.selectedType = this.byCode(this.assetTypeList, card.type_code);
            await this.$nextTick();
            this.form.subtype = this.byCode(this.subtypeList, card.subtype_id);
            // справочники блоков запускаем сразу, флаги *DictsLoaded не дадут watcher'у загрузить их повторно
            const blockDicts = this.loadBlockDicts(this.form.subtype);

            Object.assign(this.form, {
                invNumber: card.inv_number || '',
                name: card.name || '',
                ownershipType: this.byCode(this.ownershipTypeList, card.ownership_type_id),
                abp: this.byCode(this.abpList, card.abp_code),
                balanceDate: this.toDate(card.balance_date),
                region: this.byCode(this.regionList, card.region_code),
                address: card.address || '',
                initialCost: card.initial_cost ?? null,
                depreciation: card.depreciation ?? null,
                impairmentLoss: card.impairment_loss ?? null,
                revaluation: card.revaluation ?? null,
                wearAmount: card.wear_amount ?? null,
                acquisitionBasis: this.byCode(this.acquisitionBasisList, card.acquisition_basis_id),
                acquisitionNote: card.acquisition_note || '',
                balanceBasis: this.byCode(this.basisList, card.balance_basis_id),
                note: card.note || '',
                // блок reg_doc
                regDocNumber: card.reg_doc_number || '',
                regDocDate: this.toDate(card.reg_doc_date),
                regDocIssuer: card.reg_doc_issuer || '',
                cadastralNumber: card.cadastral_number || '',
                // блок building
                buildingNumber: card.building_number || '',
                buildingVolume: card.volume_m3 ?? null,
                buildingFloors: card.floors ?? null,
                buildingFlats: card.flats ?? null,
                buildingRooms: card.rooms ?? null,
                buildingAreaTotal: card.area_total ?? null,
                buildingAreaUseful: card.area_useful ?? null,
                buildingAreaAuxiliary: card.area_auxiliary ?? null,
                buildingAreaFootprint: card.area_footprint ?? null,
                buildingAreaBalcony: card.area_balcony ?? null,
                buildingAreaNonResidential: card.area_non_residential ?? null,
                buildingAreaOccupied: card.area_occupied ?? null,
                buildingAreaRented: card.area_rented ?? null,
                buildingAreaRentAvailable: card.area_rent_available ?? null,
                buildingYear: card.build_year ?? null,
                buildingLengthKm: card.length_km ?? null,
                // блок vehicle
                vehicleBrand: card.brand || '',
                vehicleStateNumber: card.state_number || '',
                vehicleEngineNumber: card.engine_number || '',
                vehicleBodyNumber: card.body_number || '',
                vehicleColor: card.color || '',
                vehicleEngineVolume: card.engine_volume ?? null,
                vehicleMileage: card.mileage ?? null,
                vehicleProductionYear: card.production_year ?? null,
                // блок equipment
                equipmentBrandType: card.brand_type || '',
                equipmentAcquisitionYear: card.acquisition_year ?? null,
                equipmentCommissioningYear: card.commissioning_year ?? null,
                // блок land
                landAreaHa: card.area_ha ?? null,
                landDivisibility: !!card.divisibility,
                landRestrictions: card.restrictions || '',
                landUseDateFrom: this.toDate(card.use_date_from),
                landUseDateTo: this.toDate(card.use_date_to),
                landRentAmount: card.rent_amount ?? null,
                // блок network
                networkWireLengthKm: card.wire_length_km ?? null,
                networkCableLengthKm: card.cable_length_km ?? null,
                networkDistributionPoints: card.distribution_points ?? null,
                networkTpSimple: card.tp_simple ?? null,
                networkTpComplete: card.tp_complete ?? null,
                networkTpOutdoor: card.tp_outdoor ?? null,
                networkSubscribers: card.subscribers ?? null,
                // блок condition
                usability: this.byCode(this.usabilityList, card.usability_id),
                commissioningDate: this.toDate(card.commissioning_date),
                techStateDesc: card.tech_state_desc || '',
                generalDesc: card.general_desc || '',
                // блок repair
                repairDate: this.toDate(card.repair_date),
                repairCost: card.repair_cost ?? null
            });

            // балансодержатель — после АБП, т.к. balanceholderOptions фильтруются по form.abp
            this.form.balanceholder = this.byCode(
                this.balanceholderOptions,
                card.balanceholder_gu_code || card.balanceholder_kgkp_bin
            );

            // выпадающие списки блоков — после загрузки их справочников
            await blockDicts;
            this.form.regDocTypeId = this.byCode(this.regDocTypeList, card.reg_doc_type_id);
            this.form.techpassportId = this.byCode(this.techpassportList, card.techpassport_id);
            this.form.buildingMaterialId = this.byCode(this.buildingMaterialList, card.material_id);
            this.form.structureTypeId = this.byCode(this.structureTypeList, card.structure_type_id);
            this.form.landPurposeId = this.byCode(this.landPurposeList, card.land_purpose_id);
        },
        async loadAssetTypeList() {
            try {
                const response = await fetch('/api-py/get_dict_asset_type');
                if (response.status === 200) {
                    const records = await response.json();
                    this.assetTypeList = (records.asset_type || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                    this.allAssetSubtypes = records.asset_subtype || [];
                    this.ownershipTypeList = (records.ownership || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                    this.acquisitionBasisList = (records.balance || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                    this.usabilityList = (records.usability || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                }
            } catch (error) {
                this.assetTypeList = [];
                this.allAssetSubtypes = [];
            }
        },
        async loadRegDocTypeList() {
            try {
                const response = await fetch('/api-py/get_dict_reg_doc_type');
                if (response.status === 200) {
                    const records = await response.json();
                    this.regDocTypeList = (records || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                }
            } catch (error) {
                this.regDocTypeList = [];
            }
        },
        async loadTechpassportList() {
            try {
                const response = await fetch('/api-py/get_dict_techpassport');
                if (response.status === 200) {
                    const records = await response.json();
                    this.techpassportList = (records || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                }
            } catch (error) {
                this.techpassportList = [];
            }
        },
        async loadBuildingMaterialList() {
            try {
                const response = await fetch('/api-py/get_dict_material');
                if (response.status === 200) {
                    const records = await response.json();
                    this.buildingMaterialList = (records || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                }
            } catch (error) {
                this.buildingMaterialList = [];
            }
        },
        async loadStructureTypeList() {
            try {
                const response = await fetch('/api-py/get_dict_structure_type');
                if (response.status === 200) {
                    const records = await response.json();
                    this.structureTypeList = (records || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                }
            } catch (error) {
                this.structureTypeList = [];
            }
        },
        async loadLandList() {
            try {
                const response = await fetch('/api-py/get_dict_type_land');
                if (response.status === 200) {
                    const records = await response.json();
                    this.landPurposeList = (records || []).map((rec) => ({
                        code: rec.code,
                        name: this.$i18n.locale === 'kk'
                            ? (rec.name_kk || rec.name_ru)
                            : (rec.name_ru || rec.name_kk)
                    }));
                }
            } catch (error) {
                this.landPurposeList = [];
            }
        },
        async loadAbpList() {
            try {
                // бэк ограничивает консультанта областью (RQ.eAk.ASSETS.01.04.07)
                const response = await fetch('/api-py/get_abp_by_user_gu_gkkp/' + this.form.balanceholderType + '/' + this.$store.getters.user_uuid
                    + '?obl=' + encodeURIComponent(this.obl)
                    // закрытых ГУ и КГКП нет только при создании(RQ.eAk.ASSETS.01.06.08)
                    + (this.isNew ? '&only_active=true' : ''));
                if (response.status === 200) {
                    const records = await response.json();
                    this.abpList = (records.abp_list || []).map(rec => ({
                        code: rec.abp,
                        name: this.$i18n.locale === 'kk' ? (rec.name_kk || rec.name_ru) : (rec.name_ru || rec.name_kk),
                        text: `${rec.abp} - ${this.$i18n.locale === 'kk' ? (rec.name_kk || rec.name_ru) : (rec.name_ru || rec.name_kk)}`
                    }));
                    this.guGkkpList = records.gu_gkkp_list || [];
                }
            } catch (error) {
                this.abpList = [];
                this.guGkkpList = [];
            }
        },
        async getObl() {
            try {
                await fetch('/api-py/get-budget-obl/' + this.$store.state._instanceCode)
                    .then(response => response.json())
                    .then(json => {
                        this.obl = json.obl;
                    });

            } catch (error) {
                this.makeToast('danger', 'Ошибка запроса getObl', error.toString());
            }
            try {
                const response = await fetch('/api-py/get_region_from_dict_kato_stat/' + this.obl);
                this.regionList = await response.json();
            } catch (error) {
                this.makeToast('danger', 'Ошибка запроса getObl', error.toString());
            }
        },
        cancel() {
            this.$router.push({ name: 'register-assets' });
        },
        // даты в JSON сводятся к ISO-строкам — для сравнения «было / стало» этого достаточно
        serializeForm() {
            return JSON.stringify(this.form);
        },
        isDirty() {
            return this.snapshot !== null && this.serializeForm() !== this.snapshot;
        },
        // «Редактировать» в просмотре: тот же маршрут, меняется только query — карточка не перезагружается
        startEdit() {
            this.$router.replace({ query: { mode: 'edit' } });
        },
        // выход из редактирования к точке запуска (RQ.eAk.ASSETS.12.05.04–06)
        async leaveEdit() {
            this.invalid = { name: false, invNumber: false, type: false, subtype: false, abp: false, balanceholder: false, balanceDate: false };
            if (this.fromRegistry) {
                this.cancel();
                return;
            }
            await this.fillForm(this.loadedCard);
            await this.$nextTick();
            this.snapshot = this.serializeForm();
            await this.$router.replace({ query: {} });
        },
        // перечитывает карточку после сохранения;
        // справочники уже загружены, поэтому только get_asset_card
        async refreshCard() {
            this.invalid = { name: false, invNumber: false, type: false, subtype: false, abp: false, balanceholder: false, balanceDate: false };
            const response = await fetch('/api-py/get_asset_card/' + this.$route.params.id
                + '?user_id=' + encodeURIComponent(this.$store.getters.user_uuid));
            if (response.status !== 200) return;
            const card = await response.json();
            this.loadedCard = card;
            this.statusId = card.status_id;
            await this.fillForm(card);
            await this.$nextTick();
            this.snapshot = this.serializeForm();
        },
        async back() {
            if (!this.isEditing) {
                this.cancel();
                return;
            }
            // NTF.eAk.ASSETS.12.04 — подтверждение выхода с несохранёнными изменениями (RQ.eAk.ASSETS.12.05.01–02)
            if (this.isDirty()) {
                const ok = await this.$bvModal.msgBoxConfirm(
                    this.label('Имеются несохранённые изменения. Вы уверены, что хотите выйти?',
                        'Сақталмаған өзгерістер бар. Шыққыңыз келетініне сенімдісіз бе?'),
                    {
                        okTitle: this.label('Выйти', 'Шығу'),
                        cancelTitle: this.label('Отменить', 'Болдырмау'),
                        centered: true
                    });
                if (!ok) return;
            }
            await this.leaveEdit();
        },
        // обязательные поля ядра, редактируемые в UC.eAk.ASSETS.12 (RQ.eAk.ASSETS.12.03.01)
        validate() {
            this.invalid = {
                name: !String(this.form.name || '').trim(),
                invNumber: !String(this.form.invNumber || '').trim()
            };
            return !this.invalid.name && !this.invalid.invNumber && !!this.form.balanceDate;
        },
        // сохранение изменений карточки (UC.eAk.ASSETS.12) — register_assets.update_asset_card
        async update() {
            // без изменений не сохраняем и не трогаем updated_at / updated_by (RQ.eAk.ASSETS.12.04.03)
            if (!this.isDirty()) {
                this.makeToast('info', this.label('Сообщение', 'Хабарлама'),
                    this.label('Изменений нет', 'Өзгерістер жоқ'));
                return;
            }
            if (!this.validate()) {
                // NTF.eAk.ASSETS.12.02
                this.makeToast('danger', this.label('Предупреждение', 'Ескерту'),
                    this.label('Заполните обязательные поля, отмеченные звёздочкой.',
                        'Жұлдызшамен белгіленген міндетті өрістерді толтырыңыз.'));
                return;
            }
            const item = this.buildPayload();
            this.$set(item, 'updated_by', this.$store.getters.user_uuid);
            try {
                const response = await fetch('/api-py/update_asset_card/' + this.$route.params.id, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json;charset=utf-8'
                    },
                    body: JSON.stringify(item)
                });
                const result = await response.json().catch(() => ({}));
                if (response.status === 409 && result.detail === 'inv_number_duplicate') {
                    // NTF.eAk.ASSETS.12.03
                    this.invalid.invNumber = true;
                    this.makeToast('danger', this.label('Предупреждение', 'Ескерту'),
                        this.label('Объект с таким инвентарным номером уже зарегистрирован у данного балансодержателя.',
                            'Бұл түгендеу нөмірімен объект осы баланс ұстаушыда бұрыннан тіркелген.'));
                    return;
                }
                if (response.status === 422 && result.detail === 'inv_number_too_long') {
                    this.invalid.invNumber = true;
                    this.makeToast('danger', this.label('Предупреждение', 'Ескерту'),
                        this.label('Инвентарный номер не должен превышать 50 символов.',
                            'Түгендеу нөмірі 50 таңбадан аспауы керек.'));
                    return;
                }
                // if (response.status === 409 && result.detail === 'archived') {
                //     // NTF.eAk.ASSETS.12.05 — карточку исключили, пока её правили
                //     await this.$bvModal.msgBoxOk(
                //         this.label('Объект исключён из реестра. Изменения не сохранены.',
                //             'Объект тізілімнен шығарылған. Өзгерістер сақталмады.'),
                //         { centered: true });
                //     await this.loadCard();
                //     return;
                // }
                if (response.status !== 200) {
                    throw this.label('Ошибка сохранения карточки объекта', 'Объект карточкасын сақтау қатесі');
                }
                // NTF.eAk.ASSETS.12.01
                this.makeToast('success', this.label('Сообщение', 'Хабарлама'),
                    this.label('Карточка объекта успешно сохранена.', 'Объект карточкасы сәтті сақталды.'));
                // после сохранения открываем форму просмотра (без перехода в реестр);
                // сохранённое состояние — новая точка отсчёта для «есть ли несохранённые изменения»
                await this.refreshCard();
                await this.$router.replace({ query: {} });
            } catch (e) {
                this.makeToast('danger', this.label('Предупреждение', 'Ескерту'), e.toString());
            }
        },
        // техническое удаление карточки (UC.eAk.ASSETS.13) — register_assets.delete_asset_card
        async confirmDelete() {
            const ok = await this.$bvModal.msgBoxConfirm(
                this.label('Карточка объекта будет удалена без возможности восстановления.',
                    'Объект карточкасы қалпына келтіру мүмкіндігінсіз жойылады.'),
                {
                    title: this.label('Удалить карточку объекта', 'Объект карточкасын жою'),
                    okTitle: this.label('Удалить', 'Жою'),
                    okVariant: 'danger',
                    cancelTitle: this.label('Отменить', 'Болдырмау'),
                    hideHeaderClose: false,
                    centered: true
                });
            if (!ok) return;
            try {
                const response = await fetch('/api-py/delete_asset_card/' + this.$route.params.id
                    + '?user_id=' + encodeURIComponent(this.$store.getters.user_uuid), { method: 'DELETE' });
                const result = await response.json().catch(() => ({}));
                if (response.status === 404 && result.detail === 'already_deleted') {
                    // NTF.eAk.ASSETS.13.02
                    await this.$bvModal.msgBoxOk(
                        this.label('Карточка объекта уже удалена.', 'Объект карточкасы бұрын жойылған.'),
                        { centered: true });
                } else if (response.status === 200) {
                    // NTF.eAk.ASSETS.13.01 — через $root: тост компонента уничтожается вместе с ним
                    // при переходе в реестр, а уведомление по спецификации выводится уже в реестре
                    this.$root.$bvToast.toast(this.label('Карточка объекта удалена.', 'Объект карточкасы жойылды.'), {
                        title: this.label('Сообщение', 'Хабарлама'),
                        variant: 'success',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 5000,
                        appendToast: true
                    });
                } else {
                    throw this.label('Ошибка удаления карточки объекта', 'Объект карточкасын жою қатесі');
                }
                this.cancel();
            } catch (e) {
                this.makeToast('danger', this.label('Предупреждение', 'Ескерту'), e.toString());
            }
        },
        // поля карточки для save_asset_card / update_asset_card — общие для создания и редактирования
        buildPayload() {
            const item = Object.assign({});
            this.$set(item, 'inv_number', this.form.invNumber);
            this.$set(item, 'name', this.form.name);
            this.$set(item, 'subtype_id', this.form.subtype.code);
            this.$set(item, 'ownership_type_id', this.form.ownershipType?.code ?? null);
            this.$set(item, 'abp_code', this.form.abp.code);
            if (this.form.balanceholderType === 'gu') {
                this.$set(item, 'balanceholder_gu_code', this.form.balanceholder.code);
                this.$set(item, 'balanceholder_kgkp_bin', null);
            } else {
                this.$set(item, 'balanceholder_gu_code', null);
                this.$set(item, 'balanceholder_kgkp_bin', this.form.balanceholder.code);
            }
            this.$set(item, 'balance_date', this.form.balanceDate ? moment(this.form.balanceDate).format('YYYY-MM-DD') : null);
            this.$set(item, 'region_code', this.form.region?.code ?? null);
            this.$set(item, 'address', this.form.address);

            this.$set(item, 'initial_cost', this.form.initialCost);
            this.$set(item, 'depreciation', this.form.depreciation);
            this.$set(item, 'impairment_loss', this.form.impairmentLoss);
            this.$set(item, 'book_value', this.bookValue);
            this.$set(item, 'revaluation', this.form.revaluation);
            this.$set(item, 'book_value_after_reval', this.bookValueAfterReval);
            this.$set(item, 'wear_amount', this.form.wearAmount);

            this.$set(item, 'acquisition_basis_id', this.form.acquisitionBasis?.code ?? null);
            this.$set(item, 'acquisition_note', this.form.acquisitionNote);
            this.$set(item, 'balance_basis_id', this.form.balanceBasis?.code ?? null);
            this.$set(item, 'note', this.form.note);

            // блок reg_doc — добавляем только если подвид его требует
            if (this.hasRegDoc) {
                this.$set(item, 'reg_doc_type_id', this.form.regDocTypeId?.code);
                this.$set(item, 'reg_doc_number', this.form.regDocNumber);
                this.$set(item, 'reg_doc_date', this.form.regDocDate ? moment(this.form.regDocDate).format('YYYY-MM-DD') : null);
                this.$set(item, 'reg_doc_issuer', this.form.regDocIssuer);
                this.$set(item, 'cadastral_number', this.form.cadastralNumber);
                this.$set(item, 'techpassport_id', this.form.techpassportId?.code);
            }

            // блок building — добавляем только если подвид его требует
            if (this.hasBuilding) {
                this.$set(item, 'building_number', this.form.buildingNumber);
                this.$set(item, 'volume_m3', this.form.buildingVolume);
                this.$set(item, 'floors', this.form.buildingFloors);
                this.$set(item, 'flats', this.form.buildingFlats);
                this.$set(item, 'rooms', this.form.buildingRooms);
                this.$set(item, 'area_total', this.form.buildingAreaTotal);
                this.$set(item, 'area_useful', this.form.buildingAreaUseful);
                this.$set(item, 'area_auxiliary', this.form.buildingAreaAuxiliary);
                this.$set(item, 'area_footprint', this.form.buildingAreaFootprint);
                this.$set(item, 'area_balcony', this.form.buildingAreaBalcony);
                this.$set(item, 'area_non_residential', this.form.buildingAreaNonResidential);
                this.$set(item, 'area_occupied', this.form.buildingAreaOccupied);
                this.$set(item, 'area_rented', this.form.buildingAreaRented);
                this.$set(item, 'area_rent_available', this.form.buildingAreaRentAvailable);
                this.$set(item, 'material_id', this.form.buildingMaterialId?.code);
                this.$set(item, 'build_year', this.form.buildingYear);
                this.$set(item, 'structure_type_id', this.form.structureTypeId?.code);
                this.$set(item, 'length_km', this.form.buildingLengthKm);
            }

            // блок vehicle — добавляем только если подвид его требует
            if (this.hasVehicle) {
                this.$set(item, 'brand', this.form.vehicleBrand);
                this.$set(item, 'state_number', this.form.vehicleStateNumber);
                this.$set(item, 'engine_number', this.form.vehicleEngineNumber);
                this.$set(item, 'body_number', this.form.vehicleBodyNumber);
                this.$set(item, 'color', this.form.vehicleColor);
                this.$set(item, 'engine_volume', this.form.vehicleEngineVolume);
                this.$set(item, 'mileage', this.form.vehicleMileage);
                this.$set(item, 'production_year', this.form.vehicleProductionYear);
            }

            // блок equipment — добавляем только если подвид его требует
            if (this.hasEquipment) {
                this.$set(item, 'brand_type', this.form.equipmentBrandType);
                this.$set(item, 'acquisition_year', this.form.equipmentAcquisitionYear);
                this.$set(item, 'commissioning_year', this.form.equipmentCommissioningYear);
            }

            // блок land — добавляем только если подвид его требует
            if (this.hasLand) {
                this.$set(item, 'area_ha', this.form.landAreaHa);
                this.$set(item, 'land_purpose_id', this.form.landPurposeId?.code);
                this.$set(item, 'divisibility', this.form.landDivisibility);
                this.$set(item, 'restrictions', this.form.landRestrictions);
                this.$set(item, 'use_date_from', this.form.landUseDateFrom ? moment(this.form.landUseDateFrom).format('YYYY-MM-DD') : null);
                this.$set(item, 'use_date_to', this.form.landUseDateTo ? moment(this.form.landUseDateTo).format('YYYY-MM-DD') : null);
                this.$set(item, 'rent_amount', this.form.landRentAmount);
            }

            // блок network — добавляем только если подвид его требует
            if (this.hasNetwork) {
                this.$set(item, 'wire_length_km', this.form.networkWireLengthKm);
                this.$set(item, 'cable_length_km', this.form.networkCableLengthKm);
                this.$set(item, 'distribution_points', this.form.networkDistributionPoints);
                this.$set(item, 'tp_simple', this.form.networkTpSimple);
                this.$set(item, 'tp_complete', this.form.networkTpComplete);
                this.$set(item, 'tp_outdoor', this.form.networkTpOutdoor);
                this.$set(item, 'subscribers', this.form.networkSubscribers);
            }

            // блок condition — добавляем только если подвид его требует
            if (this.hasCondition) {
                this.$set(item, 'usability_id', this.form.usability?.code ?? null);
                this.$set(item, 'commissioning_date', this.form.commissioningDate ? moment(this.form.commissioningDate).format('YYYY-MM-DD') : null);
                this.$set(item, 'tech_state_desc', this.form.techStateDesc);
                this.$set(item, 'general_desc', this.form.generalDesc);
            }

            // блок repair — добавляем только если подвид его требует
            if (this.hasRepair) {
                this.$set(item, 'repair_date', this.form.repairDate ? moment(this.form.repairDate).format('YYYY-MM-DD') : null);
                this.$set(item, 'repair_cost', this.form.repairCost);
            }
            // очищенное числовое поле (v-model.number) даёт "" — на бэке это не int/float, шлём null
            Object.keys(item).forEach(key => {
                if (item[key] === '' && !['inv_number', 'name'].includes(key)) {
                    item[key] = null;
                }
            });
            return item;
        },
        // обязательные поля при создании (RQ.eAk.ASSETS.01.02.01); тип балансодержателя всегда выбран (по умолчанию «ГУ»)
        validateNew() {
            this.invalid = {
                name: !String(this.form.name || '').trim(),
                invNumber: !String(this.form.invNumber || '').trim(),
                type: !this.selectedType,
                subtype: !this.form.subtype,
                abp: !this.form.abp,
                balanceholder: !this.form.balanceholder,
                balanceDate: !this.form.balanceDate
            };
            return !Object.values(this.invalid).some(Boolean) && !!this.form.balanceholderType;
        },
        async save() {
            // АС1, RQ.eAk.ASSETS.01.02.02 — NTF.eAk.ASSETS.01.02
            if (!this.validateNew()) {
                this.makeToast('danger', this.label('Предупреждение', 'Ескерту'),
                    this.label('Заполните обязательные поля, отмеченные звёздочкой.',
                        'Жұлдызшамен белгіленген міндетті өрістерді толтырыңыз.'));
                return;
            }
            this.assetCard = [];
            const item = this.buildPayload();
            this.$set(item, 'status_id', 1);
            this.$set(item, 'source_type', 'manual');
            this.$set(item, 'source_report_id', null);

            this.$set(item, 'created_at', moment().format('YYYY-MM-DD HH:mm:ss'));
            this.$set(item, 'created_by', this.$store.getters.user_uuid);
            this.isLoad = true;
            this.assetCard.push(item);
            try {
                const response = await fetch('/api-py/save_asset_card', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json;charset=utf-8'
                    },
                    body: JSON.stringify(item)
                });
                const result = await response.json().catch(() => ({}));
                if (response.status === 409 && result.detail === 'inv_number_duplicate') {
                    // NTF.eAk.ASSETS.01.03
                    this.invalid.invNumber = true;
                    this.makeToast('danger', this.label('Предупреждение', 'Ескерту'),
                        this.label('Объект с таким инвентарным номером уже зарегистрирован у данного балансодержателя.',
                            'Бұл түгендеу нөмірімен объект осы баланс ұстаушыда бұрыннан тіркелген.'));
                    return;
                }
                if (response.status === 422 && result.detail === 'inv_number_too_long') {
                    this.invalid.invNumber = true;
                    this.makeToast('danger', this.label('Предупреждение', 'Ескерту'),
                        this.label('Инвентарный номер не должен превышать 50 символов.',
                            'Түгендеу нөмірі 50 таңбадан аспауы керек.'));
                    return;
                }
                if (response.status === 200 && result.id) {
                    this.makeToast('success', this.label('Сообщение', 'Хабарлама'),
                        this.label('Карточка объекта успешно сохранена.', 'Объект карточкасы сәтті сақталды.'));
                    // UC.eAk.ASSETS.03: после сохранения открываем просмотр созданной карточки
                    await this.$router.replace({ name: 'register-assets-card', params: { id: String(result.id) } });
                } else {
                    throw this.label('Ошибка сохранения карточки объекта', 'Объект карточкасын сақтау қатесі');
                }
            } catch (e) {
                this.makeToast('danger', this.label('Предупреждение', 'Ескерту'), e.toString());
            } finally {
                this.isLoad = false;
            }
        },
        makeToast(variant, title, tostbody) {
            // через $root, чтобы тост не уничтожался вместе с компонентом при уходе на другую страницу (cancel после save)
            this.$root.$bvToast.toast(tostbody, {
                title: title,
                variant: variant,
                toaster: 'b-toaster-top-center',
                autoHideDelay: 5000,
                appendToast: true
            });
        },
    }
};
</script>

<style scoped>
.error-field ::v-deep .multiselect__tags,
.error-field ::v-deep .mx-input,
input.error-field {
    border-color: #dc3545 !important;
    background-color: #fff5f5 !important;
}
.section-header {
    background-color: #FFF5DA;
    padding: 10px 12px 10px 20px;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    color: #3F4D67;
    display: flex;
    justify-content: space-between;
}

.section-header-unit {
    text-transform: none;
    color: #7C9DB3;
}
.owner-type-block {
    display: flex;
    gap: 40px;
}
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 60;
    background: #fff;
    padding-top: 8px;
}

.status-pill {
    display: inline-block;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 8px;
}

.status-pill--active {
    background-color: #fff;
    border: 1px solid #2196F3;
    color: #2196F3;
}

.status-pill--archive {
    background-color: #fff;
    border: 1px solid #7C9DB3;
    color: #7C9DB3;
}

.asset-form-body {
    position: relative;
}

.asset-form-body::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    background-color: #E1E5EC;
    transform: translateX(-50%);
    z-index: 61;
}

@media (max-width: 768px) {
    .asset-form-body::before {
        display: none;
    }
}
</style>
